import { useState } from "react";
import { DownArrow } from "../svg";
import MinusButton from "@/components/minus-button";
import useNoInitialRenderEffect from "../../hooks/useNoInitialRenderEffect";
import Checkbox from "../checkbox";
import classNames from "classnames";
import { ITEM_CATEGORIES, MIN_ITEM_DESCRIPTION_LENGTH } from "@/lib/constants";

export default function Item({
  itemIndex,
  packageIndex,
  itemObj,
  parcel,
  updatePackage,
  validateInput,
  packingType,
  setItemValueUpdateCounter,
}) {
  const [quantity, setQuantity] = useState(itemObj.quantity ?? "");
  const [description, setDescription] = useState(itemObj.description ?? "");
  const [category, setCategory] = useState(itemObj.category ?? null);
  const [totalValue, setTotalValue] = useState(itemObj.totalValue ?? "");
  const [totalWeight, setTotalWeight] = useState(itemObj.totalWeight ?? "");

  const descErrorMsg = `Description must be ${MIN_ITEM_DESCRIPTION_LENGTH}-70 characters long`;
  const [isFragile, setIsFragile] = useState(itemObj.fragile ?? true);

  const updateItem = () => {
    const itemDetails = {
      quantity: quantity,
      description: description,
      category: category,
      totalValue: totalValue,
      totalWeight: totalWeight,
      fragile: isFragile,
    };
    updatePackage("item", itemDetails, packageIndex, itemIndex);
  };

  useNoInitialRenderEffect(
    () => updateItem(),
    [quantity, description, category, totalValue, totalWeight, isFragile]
  );

  const updateValidFields = (field, value) => {
    updatePackage("item", { [field]: value }, packageIndex, itemIndex);
  };
  return (
    <div className="flex flex-col my-2">
      {/* input row */}
      <div
        className={classNames(
          "rounded-xl group border border-border-dark-gray shadow-border",
          "grid grid-cols-[38px_auto_18%_15%_15.5%] gap-x-1.5 relative h-[36px] w-full"
        )}
      >
        <label htmlFor="quantity" hidden>
          quantity
        </label>
        <input
          type="text"
          inputMode="numeric"
          id={`quantity-p${packageIndex}i${itemIndex}`}
          className={`focus:outline-none px-1 border-2 border-transparent placeholder:text-placeholder-gray ring-[1.5px] focus:ring-light-purple rounded-xl ${
            itemObj.validQuantity !== "none" && !itemObj.validQuantity
              ? " ring-error-red"
              : "ring-transparent"
          }`}
          placeholder="8"
          value={quantity}
          onBlur={(event) => {
            if (!event.target.value) updateValidFields("validQuantity", false);
            else updateValidFields("validQuantity", true);
          }}
          onChange={(event) =>
            validateInput("number", event.target.value, (value) =>
              setQuantity(value)
            )
          }
        />
        <div
          className={`${
            itemObj.validDescription !== "none" && !itemObj.validDescription
              ? "  ring-error-red"
              : "ring-transparent"
          } flex items-center relative ring-[1.5px] focus-within:ring-light-purple rounded-xl group`}
        >
          <label htmlFor="description" hidden>
            item description
          </label>
          <input
            type="text"
            id={`description-p${packageIndex}i${itemIndex}`}
            className={`focus:outline-none placeholder:text-placeholder-gray  px-1 h-full rounded-xl w-full`}
            placeholder="Used men’s cotton T-shirts, size L, blue"
            value={description}
            maxLength="70"
            onBlur={(event) => {
              if (
                !event.target.value ||
                event.target.value.length < MIN_ITEM_DESCRIPTION_LENGTH
              )
                updateValidFields("validDescription", false);
              else updateValidFields("validDescription", true);
            }}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        <div
          className={`flex flex-wrap items-center rounded-xl  ring-[1.5px] focus-within:ring-light-purple relative ${
            !itemObj.validCategory ? " ring-error-red" : "ring-transparent"
          }`}
        >
          <select
            id={`category-p${packageIndex}i${itemIndex}`}
            className={`focus:outline-none appearance-none bg-transparent w-full h-full rounded-xl mx-0.5 ${
              !category ? "text-placeholder-gray" : ""
            }`}
            defaultValue={category ?? "none"}
            onBlur={(event) => {
              if (!event.target.value)
                updateValidFields("validCategory", false);
              else updateValidFields("validCategory", true);
            }}
            onChange={(event) => setCategory(event.target.value)}
          >
            <option value="none" className="disabled:bg-none" disabled>
              Category
            </option>
            {ITEM_CATEGORIES.map((category) => (
              <option
                key={category.value}
                value={category.value}
                className="text-black"
              >
                {category.text}
              </option>
            ))}
          </select>
          {!category ? (
            <DownArrow
              color="#844FFA"
              className="absolute right-3 w-[12px] h-[8px] pointer-events-none"
            />
          ) : null}
        </div>
        <label htmlFor="total value" hidden>
          item total value
        </label>
        <div
          className={` px-1 flex items-center border-transparent ring-[1.5px] focus-within:ring-light-purple rounded-xl ${
            itemObj.validTotalValue !== "none" && !itemObj.validTotalValue
              ? " ring-error-red "
              : "ring-transparent"
          }`}
        >
          <p className="text-dark-purple">S$</p>
          <input
            type="text"
            inputMode="decimal"
            id={`total-value-p${packageIndex}i${itemIndex}`}
            className="focus:outline-none placeholder:text-placeholder-gray  rounded-xl w-full h-full px-1"
            placeholder="60"
            value={totalValue}
            onBlur={(event) => {
              if (event.target.value > 0)
                updateValidFields("validTotalValue", true);
              else updateValidFields("validTotalValue", false);
              setItemValueUpdateCounter(
                (itemValueUpdateCounter) => itemValueUpdateCounter + 1
              );
            }}
            onChange={(event) =>
              validateInput("float", event.target.value, (value) =>
                setTotalValue(value)
              )
            }
          />
        </div>
        <label htmlFor="total weight" hidden>
          item total weight
        </label>
        <div
          className={` flex items-center border-transparent   justify-end pr-2 ring-[1.5px] focus-within:ring-light-purple rounded-xl ${
            !itemObj.validTotalWeight && itemObj.validTotalWeight !== "none"
              ? " ring-error-red"
              : "ring-transparent"
          }`}
        >
          <input
            type="text"
            inputMode="decimal"
            id={`total-weight-p${packageIndex}i${itemIndex}`}
            className="focus:outline-none text-right placeholder:text-placeholder-gray  rounded-xl w-[60%] h-full px-1 "
            placeholder="4.0"
            value={totalWeight}
            onBlur={(event) => {
              if (event.target.value > 0)
                updateValidFields("validTotalWeight", true);
              else updateValidFields("validTotalWeight", false);
            }}
            onChange={(event) =>
              validateInput("float", event.target.value, (value) =>
                setTotalWeight(value)
              )
            }
          />
          <p className="text-dark-purple">kg</p>
        </div>
        {parcel.items.length > 1 ? (
          <MinusButton
            className="absolute -right-2.5 -top-2.5 hover:cursor-pointer hidden group-hover:flex"
            onClick={() =>
              updatePackage("removeItem", null, packageIndex, itemIndex)
            }
          />
        ) : null}
      </div>
      <div className="flex mb-6 pl-12 w-full select-none justify-between">
        <p
          className={` h-1 text-xs ${
            itemObj.validDescription === false ? "text-red-500" : "invisible"
          }`}
        >
          {descErrorMsg}
        </p>

        {packingType === "Fragile" ? (
          <>
            <Checkbox
              id={`fragile-item-p${packageIndex}i${itemIndex}`}
              label="Fragile"
              containerClassName="flex items-center mt-1 justify-self-end"
              checked={isFragile}
              onChange={() => setIsFragile(!isFragile)}
              className="w-[15px] h-[15px] mr-1"
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
